// §4.3 Locations Browser (office).
//
// Tree pane (drag to re-parent — the SERVICE revalidates the materialized path,
// §2.1) + detail pane (code, name, kind, countable/active/allow-negative
// toggles) + contents table (product, lot, unit, on_hand). Virtual/system
// locations sit in their own collapsed group and are read-only: §2.4's legality
// matrix is built on them, and PATCHing one is refused
// (`VIRTUAL_LOCATION_IMMUTABLE`), so the UI does not offer the controls.
//
// "Print QR labels" is LIVE as of the module-02 QR print-kit slice: POST
// /v1/inventory/locations/qr-labels (§5 row 5) renders a real sheet through
// Module 02's print surface. It shipped visible-but-disabled while the renderer
// did not exist; the per-row selection checkboxes it is the only consumer of
// come back with it, because an input with no possible outcome is a dead end
// one level down from a dead button.
//
// The button counts its selection ("Print QR labels (3)") and is disabled with
// a reason when nothing is ticked, so it never fires an empty batch. The PDF
// arrives as the file registry's 5-minute link, which the registry serves as an
// attachment named `qr-kit-{batch}.pdf` (D-322 ruling 4), so the button follows
// it in place and the browser downloads the sheet without leaving this screen.
// A new tab would be the popup blocker's to refuse, because the link arrives
// after an await and no longer counts as the click. If the link comes back null
// (object storage briefly unreachable) the batch is still ready, and the note
// says to fetch it from the kit list rather than implying the print failed.
//
// RE-PARENTING is a subtree rewrite (§2.1: the service rewrites the
// materialized path of every descendant). It is therefore staged into a named
// confirmation — "Move A-03 and its 12 children under WH2?" — and a drop onto
// the node's own descendant is refused client-side rather than offered and
// then 422'd `LOCATION_HIERARCHY_INVALID`. Drag is not the only path: the
// detail pane carries a Parent select, which is the keyboard and touch route
// (an office iPad cannot drag at all).

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
// The register's labelled-control Field, aliased: this area's own `Field` is
// the dt/dd detail row from inventory-ui.
import { CheckboxField, Field as FormField } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { IconCaretDown } from "../../../../components/icons";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { AdjustStockLink } from "../components/adjust-stock-link";
import { FacilityChip } from "../components/filters";
import { Field, RefLabel } from "../components/inventory-ui";
import { LocationPicker } from "../components/location-picker";
import {
  useCreateLocation,
  useFacilities,
  useLocationContents,
  useLocationPages,
  useLocationPathIndex,
  usePrintLocationQrLabels,
  useLotIndex,
  usePatchLocation,
  useProductIndex,
  useStockUnitIndex,
  type InventoryLocation,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { isAdjustablePlace, placeEntrySearch } from "../lib/adjust";
import { inventoryErrorMessage } from "../lib/errors";
import { formatMeasureWithUom } from "../lib/measure";

type S = components["schemas"];

/** §2.1: the physical tree the user may shape. Everything else is a system
 * location the module creates and owns (`ensureVirtualLocations`). */
const PHYSICAL_KINDS = new Set(["warehouse", "zone", "bin"]);

interface TreeNode {
  readonly location: InventoryLocation;
  readonly children: TreeNode[];
}

/**
 * Build the tree, keeping "root" and "its parent is not on this page" apart.
 * Silently promoting an orphan to a root made a >200-bin warehouse render as a
 * structurally wrong tree with no sign anything was missing.
 */
export function buildTree(locations: readonly InventoryLocation[]): {
  roots: TreeNode[];
  orphans: TreeNode[];
} {
  const nodes = new Map<string, TreeNode>();
  for (const location of locations) nodes.set(location.id, { location, children: [] });
  const roots: TreeNode[] = [];
  const orphans: TreeNode[] = [];
  for (const node of nodes.values()) {
    const parentId = node.location.parent_location_id;
    if (parentId === null) {
      roots.push(node);
      continue;
    }
    const parent = nodes.get(parentId);
    if (parent === undefined) orphans.push(node);
    else parent.children.push(node);
  }
  const sort = (list: TreeNode[]) => {
    list.sort((a, b) => a.location.code.localeCompare(b.location.code));
    for (const child of list) sort(child.children);
  };
  sort(roots);
  sort(orphans);
  return { roots, orphans };
}

/** Ids of a node and everything under it — a node may not become its own child. */
export function subtreeIds(node: TreeNode): Set<string> {
  const out = new Set<string>();
  const walk = (current: TreeNode) => {
    out.add(current.location.id);
    for (const child of current.children) walk(child);
  };
  walk(node);
  return out;
}

/** A node from EITHER list — the roots or the off-page orphans. Both are real
 * places a node lives, and a lookup that knew only about one was task #251. */
function locateNode(
  roots: readonly TreeNode[],
  orphans: readonly TreeNode[],
  id: string,
): TreeNode | undefined {
  return findNode(roots, id) ?? findNode(orphans, id);
}

function findNode(nodes: readonly TreeNode[], id: string): TreeNode | undefined {
  for (const node of nodes) {
    if (node.location.id === id) return node;
    const hit = findNode(node.children, id);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

export function LocationsBrowserScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();

  const [facilityId, setFacilityId] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [showVirtual, setShowVirtual] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ dragId: string; targetId: string } | null>(null);
  const [moved, setMoved] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [printNote, setPrintNote] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");

  // SEARCH (added 2026-09-21, D-295). Debounced at the same 250 ms as every
  // other location search in this module (`components/location-picker.tsx`):
  // the tree is thousands of rows on a real tenant and a request per keystroke
  // over it is a self-inflicted load test.
  const q = useDebouncedValue(searchText, 250).trim();
  const searching = q.length > 0;

  const query = useMemo(
    () => ({
      limit: 200,
      ...(facilityId.length > 0 ? { facility_id: facilityId } : {}),
      ...(q.length > 0 ? { q } : {}),
    }),
    [facilityId, q],
  );
  // PAGES, not a page. The old read took ONE 200-row page and drew a tree from
  // it: a warehouse with 300 bins rendered as a structurally wrong tree whose
  // missing half showed up only as "orphans". The tree is now built from every
  // page the user has asked for, the row count is on screen, and "load more" is
  // an explicit act — never a silent first page, and never an unbounded drain of
  // a tenant with tens of thousands of bins.
  const locations = useLocationPages(query);
  const patch = usePatchLocation();
  const printLabels = usePrintLocationQrLabels();

  const canManage = perms.has("inventory.location.manage");
  const canPrint = perms.canPrintLabels;

  const items = useMemo(
    () => (locations.data?.pages ?? []).flatMap((page) => page.items),
    [locations.data],
  );
  const truncated = locations.hasNextPage;
  const physical = items.filter((item) => PHYSICAL_KINDS.has(item.kind));
  const virtual = items.filter((item) => !PHYSICAL_KINDS.has(item.kind));
  const { roots: tree, orphans } = useMemo(() => buildTree(physical), [physical]);
  // Resolved BY ID as a fallback, for the same reason the drop target below is:
  // the selected row need not be on any loaded page. A location picked out of a
  // SEARCH result is gone from `items` the instant the search is cleared, and a
  // detail pane that blanked at that moment would drop the very location the
  // user had just gone looking for.
  const selectedOnPage = items.find((item) => item.id === selectedId);
  // ONLY when the row is not already in hand: `useLocationPathIndex` issues a
  // real request per distinct id, and firing it for every click on a loaded
  // tree would spend one on an answer the page already holds.
  const selectedIndex = useLocationPathIndex(
    selectedId === null || selectedOnPage !== undefined ? [] : [selectedId],
  );
  const selected =
    selectedOnPage ?? (selectedId === null ? undefined : selectedIndex.byId.get(selectedId));

  useEffect(() => {
    if (locations.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [locations.error, navigate]);

  // THE SOURCE RESOLVES THE SAME THREE WAYS THE TARGET DOES (task #251).
  //
  // `tree` is the ROOTS array; `buildTree` deliberately keeps a node whose
  // parent is off-page in `orphans` instead. During a SEARCH that is the
  // normal case — a matched bin's zone is rarely in the result — so a lookup
  // in `tree` alone returned undefined, the confirmation below never
  // rendered, and the Parent select did NOTHING AT ALL on the one path §4.3's
  // search round names as the reachable one. The same hole made a drag out of
  // the orphans group inert, which is where it came from.
  const moveSourceNode =
    pendingMove === null ? undefined : locateNode(tree, orphans, pendingMove.dragId);
  // And by id for the last case: a location picked out of a search and still
  // selected after the search is cleared is on NO loaded page, so it is in
  // neither list.
  const moveSourceIndex = useLocationPathIndex(pendingMove === null ? [] : [pendingMove.dragId]);
  const moveSource =
    moveSourceNode?.location ??
    (pendingMove === null ? undefined : moveSourceIndex.byId.get(pendingMove.dragId));
  // The drop target can be a location the user picked by SEARCH (the detail
  // pane's parent picker), which is not necessarily among the pages loaded here.
  // Resolving it by id is what lets the confirmation name it instead of silently
  // refusing to open.
  const targetIndex = useLocationPathIndex(pendingMove === null ? [] : [pendingMove.targetId]);
  const moveTarget =
    pendingMove === null
      ? undefined
      : (items.find((i) => i.id === pendingMove.targetId) ??
        targetIndex.byId.get(pendingMove.targetId));
  // NULL, not 0, when the node is not in the loaded tree: its descendants are
  // genuinely unknown there, and "and its 0 descendants" would be a number the
  // screen invented. The confirmation says so in its own words instead.
  const descendantCount = moveSourceNode === undefined ? null : subtreeIds(moveSourceNode).size - 1;

  // A TICK BELONGS TO THE LIST IT WAS MADE IN. `picked` used to survive both a
  // search and a facility change, so the button could read "Print QR labels
  // (3)" for rows nowhere on screen — and §5 row 5 ANDs `facility_id`, so a
  // batch built before a facility change 404s the whole sheet. Cleared with
  // either narrowing, and the note goes with it.
  useEffect(() => {
    setPicked(new Set());
    setPrintNote(null);
  }, [q, facilityId]);

  function togglePick(id: string) {
    setPrintNote(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function requestMove(dragId: string, targetId: string) {
    const source = locateNode(tree, orphans, dragId);
    // A node cannot be dropped into its own subtree — the service refuses it
    // (LOCATION_HIERARCHY_INVALID) and offering it would be a control that
    // exists only to fail. When the source is on no loaded page its subtree is
    // unknown here, so this guard cannot run and the server's 422 is what
    // refuses instead — a later refusal, never a silent one.
    if (source !== undefined && subtreeIds(source).has(targetId)) return;
    setMoved(null);
    setPendingMove({ dragId, targetId });
  }

  return (
    <section className="locations-browser">
      <PageHeader
        title={term("location")}
        actions={
          <>
            <FacilityChip value={facilityId} onChange={setFacilityId} />
            {canManage ? (
              <Button
                variant="primary"
                onClick={() => {
                  setCreateOpen((prev) => !prev);
                }}
              >
                {t("inventory.locations.add")}
              </Button>
            ) : null}
            {/* §4.3 batch QR labels (§5 row 5). Disabled ONLY when nothing is
              ticked, and the title says so — a control that is dark for a
              reason the user can fix is not a dead control. */}
            {canPrint ? (
              <Button
                busy={printLabels.isPending}
                busyLabel={t("inventory.locations.printQrBusy")}
                disabled={picked.size === 0}
                title={picked.size === 0 ? t("inventory.locations.qrPickFirst") : undefined}
                onClick={() => {
                  setPrintNote(null);
                  printLabels.mutate(
                    {
                      location_ids: [...picked],
                      ...(facilityId.length > 0 ? { facility_id: facilityId } : {}),
                    },
                    {
                      onSuccess: (result) => {
                        if (result.pdf_url === null) {
                          setPrintNote(t("inventory.locations.qrNoLink"));
                          return;
                        }
                        // An attachment response downloads without unloading
                        // the page, so following the link in place is safe.
                        const anchor = document.createElement("a");
                        anchor.href = result.pdf_url;
                        anchor.rel = "noopener noreferrer";
                        anchor.click();
                        setPrintNote(t("inventory.locations.qrPrinted", { n: result.item_count }));
                      },
                      onError: (error: unknown) => {
                        setPrintNote(inventoryErrorMessage(t, error));
                      },
                    },
                  );
                }}
              >
                {t("inventory.locations.printQr", { n: picked.size })}
              </Button>
            ) : null}
          </>
        }
      />
      {/* §4.3's SEARCH (D-295), on its OWN ROW rather than in the page head.
          It shipped inside `page-head-actions` and the M06 responsive net
          measured it at 161 px against 176 px of its own content on a 412 px
          phone: that container has no flex rule of its own, so an inline-flex
          label with `min-width: 0` simply shrink-to-fits into whatever the
          heading leaves, and `flex: 1 1 200px` did nothing there.
          §4.1 settled this shape already — `lookup-bar` + `lookup-search`,
          whose mobile rule gives the input `width: 100%` of a stretched
          single-column bar — and that screen passes the same net at the same
          width. Reusing it also retires a byte-for-byte copy of
          `catalogue.css`'s `.cat-search` that an earlier draft of this feature
          introduced. */}
      <div className="lookup-bar">
        <label className="lookup-search">
          <span className="visually-hidden">{t("inventory.locations.searchLabel")}</span>
          <input
            type="search"
            data-testid="locations-search"
            value={searchText}
            placeholder={t("inventory.locations.searchPlaceholder")}
            onChange={(event) => {
              setSearchText(event.target.value);
              // An un-confirmed move belongs to the list it was started from.
              // Left standing, it would re-materialise as a confirmation
              // minutes later when the tree it names reloads — an unrequested
              // "move A and its 12 descendants" for something the user walked
              // away from.
              setPendingMove(null);
            }}
          />
        </label>
      </div>

      {printNote !== null ? (
        <p className="inv-hint" role="status">
          {printNote}
        </p>
      ) : null}

      {/* The count is stated whether or not more remains, so "1 240 locations"
          is a fact the user was told rather than a number they have to trust the
          tree for. When more remains, the note says so and the button next to it
          is the way to get it. */}
      {items.length > 0 ? (
        <p
          className={truncated ? "degraded-note is-warning" : "inv-hint"}
          role="note"
          data-testid="locations-count"
        >
          {searching
            ? truncated
              ? t("inventory.locations.matchesTruncated", { n: items.length })
              : t("inventory.locations.matches", { n: items.length })
            : truncated
              ? t("inventory.locations.truncated", { n: items.length })
              : t("inventory.locations.showingAll", { n: items.length })}
        </p>
      ) : null}
      {truncated ? (
        <Button
          data-testid="locations-load-more"
          busy={locations.isFetchingNextPage}
          busyLabel={t("inventory.locations.loadingMore")}
          onClick={() => {
            void locations.fetchNextPage();
          }}
        >
          {t("inventory.locations.loadMore")}
        </Button>
      ) : null}

      {createOpen && canManage ? (
        <CreateLocationPanel
          facilityId={facilityId}
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      ) : null}

      {locations.isPending ? (
        <TableSkeleton rows={6} cols={3} label={t("inventory.locations.loading")} />
      ) : null}

      {locations.error && !(locations.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.locations.errorTitle")}
          detail={locations.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void locations.refetch()}
          requestId={requestIdOf(locations.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {/* Two DIFFERENT nothings. "No locations yet" offers to create a
          warehouse; "nothing matches" must not, because the tenant's tree is
          right there behind a query the user can clear — and a CTA that
          creates a location is the wrong answer to a typo. */}
      {locations.data !== undefined && items.length === 0 && searching ? (
        <EmptyState
          title={t("inventory.locations.noMatchesTitle", { q })}
          body={t("inventory.locations.noMatchesBody")}
          ctaLabel={t("inventory.locations.clearSearch")}
          onCta={() => {
            setSearchText("");
          }}
        />
      ) : null}

      {locations.data !== undefined && items.length === 0 && !searching ? (
        <EmptyState
          title={t("inventory.locations.emptyTitle")}
          body={t("inventory.locations.emptyBody")}
          {...(canManage
            ? {
                ctaLabel: t("inventory.locations.add"),
                onCta: () => {
                  setCreateOpen(true);
                },
              }
            : {})}
        />
      ) : null}

      {locations.data !== undefined && items.length > 0 ? (
        <div className="locations-split">
          <div
            className="locations-tree"
            aria-label={
              searching ? t("inventory.locations.matchesAria") : t("inventory.locations.treeAria")
            }
          >
            {searching ? (
              <MatchList
                items={items}
                selectedId={selectedId}
                onSelect={setSelectedId}
                selectable={canPrint}
                picked={picked}
                onPick={togglePick}
              />
            ) : null}

            {/* Only when it is TRUE (rev-7 audit #26): a page of bins whose
                warehouses sit on a later page has no roots, but it has
                physical locations, shown below as orphans. Saying "no
                warehouses, zones or bins yet" over them, beside a note that
                more remain, contradicted the screen. */}
            {!searching && physical.length === 0 && !truncated ? (
              <p className="inv-hint">{t("inventory.locations.noPhysical")}</p>
            ) : null}
            {!searching && tree.length > 0 ? (
              <ul className="tree-root">
                {tree.map((node) => (
                  <TreeBranch
                    key={node.location.id}
                    node={node}
                    depth={0}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    canManage={canManage}
                    selectable={canPrint}
                    picked={picked}
                    onPick={togglePick}
                    dragId={dragId}
                    onDragStart={setDragId}
                    onDrop={(targetId) => {
                      if (dragId === null || dragId === targetId) return;
                      requestMove(dragId, targetId);
                      setDragId(null);
                    }}
                  />
                ))}
              </ul>
            ) : null}

            {!searching && orphans.length > 0 ? (
              <div className="tree-orphans">
                <p className="degraded-note is-warning" role="note">
                  {t("inventory.locations.orphans", { n: orphans.length })}
                </p>
                <ul className="tree-root">
                  {orphans.map((node) => (
                    <TreeBranch
                      key={node.location.id}
                      node={node}
                      depth={0}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      canManage={canManage}
                      selectable={canPrint}
                      picked={picked}
                      onPick={togglePick}
                      dragId={dragId}
                      onDragStart={setDragId}
                      onDrop={(targetId) => {
                        if (dragId === null || dragId === targetId) return;
                        requestMove(dragId, targetId);
                        setDragId(null);
                      }}
                    />
                  ))}
                </ul>
              </div>
            ) : null}

            {pendingMove !== null && moveSource !== undefined && moveTarget !== undefined ? (
              <div className="inv-panel move-confirm" role="alertdialog" aria-live="polite">
                <p className="inv-warning">
                  {descendantCount === null
                    ? t("inventory.locations.moveConfirmOffPage", {
                        source: moveSource.code,
                        target: moveTarget.code,
                      })
                    : t("inventory.locations.moveConfirm", {
                        source: moveSource.code,
                        target: moveTarget.code,
                        count: descendantCount,
                      })}
                </p>
                <div className="form-actions">
                  <Button
                    variant="primary"
                    busy={patch.isPending}
                    onClick={() => {
                      const move = pendingMove;
                      patch.mutate(
                        { id: move.dragId, body: { parent_location_id: move.targetId } },
                        {
                          onSuccess: () => {
                            setMoved(
                              t("inventory.locations.moved", {
                                source: moveSource.code,
                                target: moveTarget.code,
                              }),
                            );
                          },
                        },
                      );
                      setPendingMove(null);
                    }}
                  >
                    {t("inventory.common.confirm")}
                  </Button>
                  <Button
                    onClick={() => {
                      setPendingMove(null);
                    }}
                  >
                    {t("inventory.common.cancel")}
                  </Button>
                </div>
              </div>
            ) : null}

            {moved !== null ? (
              <p className="inv-success" role="status">
                {moved}
              </p>
            ) : null}

            {!searching && virtual.length > 0 ? (
              <div className="tree-virtual">
                <button
                  type="button"
                  className="tree-virtual-toggle"
                  aria-expanded={showVirtual}
                  onClick={() => {
                    setShowVirtual((prev) => !prev);
                  }}
                >
                  {t("inventory.locations.virtualGroup", { n: virtual.length })}
                </button>
                {showVirtual ? (
                  <ul className="tree-root">
                    {virtual.map((location) => (
                      <li key={location.id} className="tree-node tree-node-virtual">
                        <button
                          type="button"
                          className={`tree-label${location.id === selectedId ? " is-selected" : ""}`}
                          onClick={() => {
                            setSelectedId(location.id);
                          }}
                        >
                          {location.code} · {location.name}
                        </button>
                        <span className="tree-kind">
                          {enumLabel(t, "inventory.vocab.locationKind", location.kind)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="inv-hint">{t("inventory.locations.virtualReadOnly")}</p>
              </div>
            ) : null}

            {patch.isError ? (
              <p className="form-error" role="alert">
                {inventoryErrorMessage(t, patch.error, "inventory.locations.reparentFailed")}
              </p>
            ) : null}
          </div>

          <div className="locations-detail">
            {selected === undefined ? (
              <p className="inv-hint">{t("inventory.locations.selectPrompt")}</p>
            ) : (
              <LocationDetail
                location={selected}
                // Physical-only, because §2.4's legality matrix is built on the
                // system locations and PATCHing one is refused.
                canManage={canManage && PHYSICAL_KINDS.has(selected.kind)}
                // The raw §7 grant: `allow_negative` is offered on PRODUCTION
                // locations, which are not physical kinds, so `canManage` is
                // false there by construction and cannot gate this control.
                canEditNegative={canManage}
                onRequestMove={requestMove}
                locale={i18n.language}
              />
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * §4.3's SEARCH RESULTS — a FLAT list, on purpose.
 *
 * A filtered set has no honest tree. The matches' parents are usually outside
 * the result, and `buildTree` would promote every one of them to a root or to
 * the "orphans" group — which is precisely the structurally-wrong render the
 * 2026-08-05 truncation round existed to kill ("a warehouse with 300 bins
 * rendered as a structurally wrong tree whose missing half showed up only as
 * orphans"). Re-introducing it behind a search box would be the same defect
 * with a nicer entry point.
 *
 * So each row states its full materialized PATH instead. That is what the
 * hierarchy is FOR at this moment — "WH1/A/A-042" is what the storekeeper reads
 * off the rack label, and it is also what `q` matched on.
 *
 * VIRTUAL locations are listed here alongside physical ones: a search for
 * "quarantine" should find the quarantine location, and the detail pane already
 * renders system locations read-only (§2.4's legality matrix). Drag-to-reparent
 * is absent because there is no tree to drop onto — the detail pane's Parent
 * select is the reachable path, and it is the only one a touch device or a
 * keyboard ever had.
 */
function MatchList({
  items,
  selectedId,
  onSelect,
  selectable,
  picked,
  onPick,
}: {
  items: readonly InventoryLocation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  selectable: boolean;
  picked: ReadonlySet<string>;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ul className="tree-root location-matches" data-testid="locations-matches">
      {items.map((location) => (
        <li key={location.id} className="tree-node location-match">
          <div className="tree-row">
            {/* PHYSICAL AND ACTIVE ONLY. §5 row 5 resolves a label batch over
                active physical locations and refuses the WHOLE sheet with 404
                LOCATION_NOT_FOUND if any named id is neither — so a tick on a
                system or archived row found by search would lose the entire
                print run to an error naming nothing. The tree never offered
                one (its virtual group has no checkbox); the flat list must
                not either. */}
            {selectable && PHYSICAL_KINDS.has(location.kind) && location.is_active ? (
              <input
                type="checkbox"
                className="tree-pick"
                checked={picked.has(location.id)}
                aria-label={t("inventory.locations.pickAria", { code: location.code })}
                onChange={() => {
                  onPick(location.id);
                }}
              />
            ) : null}
            <button
              type="button"
              className={`tree-label${location.id === selectedId ? " is-selected" : ""}`}
              onClick={() => {
                onSelect(location.id);
              }}
            >
              {location.code} · {location.name}
            </button>
            <span className="tree-kind">
              {enumLabel(t, "inventory.vocab.locationKind", location.kind)}
            </span>
            {!location.is_active ? (
              <span className="status-badge status-muted">{t("inventory.locations.inactive")}</span>
            ) : null}
          </div>
          <p className="location-match-path">{location.path}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * WHICH BRANCHES START OPEN (W-SWEEP height, D-451). Every branch used to be
 * drawn open, so a 200-row page of the demo plant measured 8,167 px in
 * English, nine screens of bins nobody had asked to see. The warehouses (the
 * roots) open by default and show their zones; a zone keeps its bins folded
 * until it is opened. Search still lists every match flat, and a branch opened
 * once stays open while the reader works.
 */
export function branchStartsOpen(depth: number): boolean {
  return depth === 0;
}

function TreeBranch({
  node,
  depth,
  selectedId,
  onSelect,
  canManage,
  selectable,
  picked,
  onPick,
  dragId,
  onDragStart,
  onDrop,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  canManage: boolean;
  selectable: boolean;
  picked: ReadonlySet<string>;
  onPick: (id: string) => void;
  dragId: string | null;
  onDragStart: (id: string) => void;
  onDrop: (targetId: string) => void;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const [open, setOpen] = useState(() => branchStartsOpen(depth));
  const location = node.location;
  const hasChildren = node.children.length > 0;

  return (
    <li
      className={`tree-node${over ? " is-drop-target" : ""}`}
      style={{ paddingLeft: `${String(depth * 0.75)}rem` }}
    >
      <div
        className="tree-row"
        draggable={canManage}
        onDragStart={() => {
          onDragStart(location.id);
        }}
        onDragOver={(event) => {
          if (!canManage || dragId === null || dragId === location.id) return;
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          onDrop(location.id);
        }}
      >
        {/* The label-batch selection. Rendered only for callers who hold
            `onboarding.generate_qr_kit`: an input nobody can act on is the dead
            end this screen refused to ship while the renderer was missing. */}
        {hasChildren ? (
          <button
            type="button"
            className={`tree-toggle${open ? "" : " is-folded"}`}
            data-testid="locations-tree-toggle"
            aria-expanded={open}
            aria-label={
              open
                ? t("inventory.locations.collapseAria", { code: location.code })
                : t("inventory.locations.expandAria", {
                    code: location.code,
                    count: node.children.length,
                  })
            }
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          >
            <IconCaretDown />
          </button>
        ) : (
          <span className="tree-toggle-space" aria-hidden="true" />
        )}
        {selectable ? (
          <input
            type="checkbox"
            className="tree-pick"
            checked={picked.has(location.id)}
            aria-label={t("inventory.locations.pickAria", { code: location.code })}
            onChange={() => {
              onPick(location.id);
            }}
          />
        ) : null}
        <button
          type="button"
          className={`tree-label${location.id === selectedId ? " is-selected" : ""}`}
          onClick={() => {
            onSelect(location.id);
          }}
        >
          {location.code} · {location.name}
        </button>
        <span className="tree-kind">
          {enumLabel(t, "inventory.vocab.locationKind", location.kind)}
        </span>
        {!location.is_active ? (
          <span className="status-badge status-muted">{t("inventory.locations.inactive")}</span>
        ) : null}
        {hasChildren && !open ? (
          <span className="tree-count" data-testid="locations-tree-count">
            {t("inventory.locations.childCount", { count: node.children.length })}
          </span>
        ) : null}
      </div>
      {hasChildren && open ? (
        <ul>
          {node.children.map((child) => (
            <TreeBranch
              key={child.location.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              canManage={canManage}
              selectable={selectable}
              picked={picked}
              onPick={onPick}
              dragId={dragId}
              onDragStart={onDragStart}
              onDrop={onDrop}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function LocationDetail({
  location,
  canManage,
  canEditNegative,
  onRequestMove,
  locale,
}: {
  location: InventoryLocation;
  canManage: boolean;
  canEditNegative: boolean;
  onRequestMove: (dragId: string, targetId: string) => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const patch = usePatchLocation();
  const contents = useLocationContents(location.id);
  const levels = contents.data?.levels ?? [];
  const canAdjust = useInventoryPermissions().has("inventory.adjust");
  const products = useProductIndex(levels.map((level) => level.product_id));
  const lots = useLotIndex(
    levels.flatMap((level) => (level.lot_id !== null ? [level.lot_id] : [])),
  );
  const units = useStockUnitIndex(
    levels.flatMap((level) => (level.stock_unit_id !== null ? [level.stock_unit_id] : [])),
  );
  const [name, setName] = useState(location.name);
  const [renamed, setRenamed] = useState(false);
  // WHETHER THE PARENT PICKER IS OPEN, and the reason §4.3's Parent select has
  // never re-parented anything (task #251, second half).
  //
  // `LocationPicker` renders its CHOSEN state whenever the `value` PROP is
  // non-empty, and its "change" button is `onChange("")`. This pane fed it
  // `location.parent_location_id` and dropped an empty `next` on the floor —
  // so the press changed no prop, the picker re-rendered chosen, and the
  // search input could never appear. Every location that HAS a parent, which
  // is every zone and every bin, was unreachable: the control looked live and
  // was inert, on a touch device and a keyboard the only route there is.
  const [pickingParent, setPickingParent] = useState(false);

  // The selected location changes under this pane without a remount.
  useEffect(() => {
    setName(location.name);
    setRenamed(false);
    setPickingParent(false);
  }, [location.id, location.name]);

  function toggle(body: S["LocationPatchRequest"]) {
    patch.mutate({ id: location.id, body });
  }

  // §5 row 3 is "rename / re-parent / toggle"; only the toggles had a surface,
  // so `name` was unreachable from anywhere in the app.
  //
  // The legal parent KIND is exactly one (§2.1: zone under warehouse, bin under
  // zone), so the picker narrows on the server by `kind` and searches the whole
  // facility — the old select could only offer parents that happened to be on
  // the page this screen had loaded.
  const parentKinds = legalParentKinds(location.kind);

  return (
    <>
      <h2>
        {location.code} · {location.name}
      </h2>
      <dl className="inv-fields">
        <Field label={t("inventory.locations.kind")}>
          {enumLabel(t, "inventory.vocab.locationKind", location.kind)}
        </Field>
        <Field label={t("inventory.locations.path")}>{location.path}</Field>
        <Field label={t("inventory.locations.name")}>
          {canManage ? (
            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                patch.mutate(
                  { id: location.id, body: { name: name.trim() } },
                  {
                    onSuccess: () => {
                      setRenamed(true);
                    },
                  },
                );
              }}
            >
              <input
                className="ds-input"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setRenamed(false);
                }}
                aria-label={t("inventory.locations.name")}
                required
              />
              <Button
                type="submit"
                busy={patch.isPending}
                disabled={name.trim().length === 0 || name.trim() === location.name}
              >
                {t("inventory.locations.rename")}
              </Button>
              {renamed ? (
                <span className="inv-success" role="status">
                  {t("inventory.locations.renamed")}
                </span>
              ) : null}
            </form>
          ) : (
            location.name
          )}
        </Field>
        {parentKinds !== undefined ? (
          <Field label={t("inventory.locations.parent")}>
            {/* The keyboard / touch route to a re-parent; it goes through the
                SAME confirmation as the drag, so picking here changes nothing
                until the confirmation is accepted. */}
            <LocationPicker
              testId="location-parent"
              label={t("inventory.locations.parent")}
              isActive
              value={pickingParent ? "" : (location.parent_location_id ?? "")}
              onChange={(next) => {
                // An empty `next` is the "change" press: it OPENS the picker
                // rather than being discarded. A real choice closes it and
                // stages the move, which the confirmation still gates.
                if (next.length === 0) {
                  setPickingParent(true);
                  return;
                }
                setPickingParent(false);
                if (next !== location.id) onRequestMove(location.id, next);
              }}
              facilityId={location.facility_id}
              serverKinds={parentKinds}
              disabled={!canManage || patch.isPending}
              clearLabel={t("inventory.locations.pickParent")}
            />
          </Field>
        ) : null}
        <Field label={t("inventory.locations.countable")}>
          <CheckboxField
            label={t("inventory.locations.countableHint")}
            checked={location.is_countable}
            disabled={!canManage || patch.isPending}
            onChange={(checked) => {
              toggle({ is_countable: checked });
            }}
          />
        </Field>
        <Field label={t("inventory.locations.active")}>
          <CheckboxField
            label={t("inventory.locations.activeHint")}
            checked={location.is_active}
            disabled={!canManage || patch.isPending}
            onChange={(checked) => {
              toggle({ is_active: checked });
            }}
          />
        </Field>
        {location.kind === "production" ? (
          <Field label={t("inventory.locations.allowNegative")}>
            <CheckboxField
              label={t("inventory.locations.allowNegativeHint")}
              checked={location.allow_negative}
              disabled={!canEditNegative || patch.isPending}
              onChange={(checked) => {
                toggle({ allow_negative: checked });
              }}
            />
          </Field>
        ) : null}
        <Field label={t("inventory.locations.qrPayload")}>
          {location.qr_payload === null ? (
            <span className="inv-muted">{t("inventory.common.none")}</span>
          ) : (
            <code>{location.qr_payload}</code>
          )}
        </Field>
      </dl>

      {patch.isError ? (
        <p className="form-error" role="alert">
          {inventoryErrorMessage(t, patch.error, "inventory.locations.updateFailed")}
        </p>
      ) : null}

      <h3>{t("inventory.locations.contents")}</h3>
      {/* §4.12 (D-315): a place that holds stock opens the form on itself.
          Not on the counter locations (found, scrap, customer…) and not on
          work in progress, which the form books against rather than into. */}
      {isAdjustablePlace(location.kind) ? (
        <p>
          <AdjustStockLink
            search={{
              location_id: location.id,
              facility_id: location.facility_id,
              ...placeEntrySearch(location.kind),
              from: "location",
            }}
            label={t("inventory.locations.adjustHere")}
            testId="location-adjust-here"
          />
        </p>
      ) : null}
      {contents.isPending ? (
        <TableSkeleton rows={3} cols={4} label={t("inventory.locations.contentsLoading")} />
      ) : contents.error ? (
        <ErrorState
          title={t("inventory.locations.contentsError")}
          detail={contents.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void contents.refetch()}
          requestId={requestIdOf(contents.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : contents.data.levels.length === 0 ? (
        <EmptyState
          title={t("inventory.locations.contentsEmptyTitle")}
          body={t("inventory.locations.contentsEmptyBody")}
        />
      ) : (
        <DataTable
          columns={[
            {
              key: "product_id",
              header: t("inventory.locations.colProduct"),
              id: true,
              render: (level) => (
                <RefLabel
                  value={products.byId.get(level.product_id)?.sku}
                  id={level.product_id}
                  pending={products.isPending}
                  kind="product"
                />
              ),
            },
            {
              key: "lot_id",
              header: term("lot"),
              render: (level) =>
                level.lot_id === null ? (
                  "—"
                ) : (
                  <RefLabel
                    value={lots.byId.get(level.lot_id)?.lot_code}
                    id={level.lot_id}
                    pending={lots.isPending}
                    kind="lot"
                  />
                ),
            },
            {
              key: "stock_unit_id",
              header: term("stock_unit"),
              render: (level) =>
                level.stock_unit_id === null ? (
                  "—"
                ) : (
                  <RefLabel
                    value={units.byId.get(level.stock_unit_id)?.serial_code}
                    id={level.stock_unit_id}
                    pending={units.isPending}
                    kind="stock_unit"
                  />
                ),
            },
            {
              key: "on_hand",
              header: t("inventory.locations.colOnHand"),
              numeric: true,
              render: (level) => formatMeasureWithUom(level.on_hand, level.uom, { locale }),
            },
            // §4.12: only a LOOSE row (no container) is what a bulk adjustment
            // corrects; a container's row is corrected on its own card.
            ...(canAdjust && isAdjustablePlace(location.kind)
              ? [
                  {
                    key: "adjust",
                    header: t("inventory.common.actions"),
                    actions: true,
                    render: (level: (typeof levels)[number]) =>
                      level.stock_unit_id === null ? (
                        <AdjustStockLink
                          search={{
                            product_id: level.product_id,
                            ...(level.lot_id !== null ? { lot_id: level.lot_id } : {}),
                            location_id: location.id,
                            facility_id: location.facility_id,
                            ...placeEntrySearch(location.kind),
                            from: "location",
                          }}
                          label={t("inventory.locations.adjustRow")}
                          testId={`location-row-adjust-${level.product_id}-${level.lot_id ?? "none"}`}
                        />
                      ) : null,
                  },
                ]
              : []),
          ]}
          rows={contents.data.levels}
          rowKey={(level) =>
            `${level.product_id}-${level.lot_id ?? "x"}-${level.stock_unit_id ?? "x"}`
          }
          empty={null}
        />
      )}
    </>
  );
}

/**
 * §4.3's "add a warehouse" — the empty-state CTA has to DO something, so this is
 * the real POST /locations (§5 row 2). Kind is limited to the physical three:
 * the system/virtual locations are created by the module itself
 * (`ensureVirtualLocations`) and creating one by hand would fight §2.4.
 */
function CreateLocationPanel({ facilityId, onClose }: { facilityId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateLocation();
  const facilities = useFacilities();
  const facilityItems = facilities.data?.items ?? [];
  const [facility, setFacility] = useState(facilityId.length > 0 ? facilityId : "");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"warehouse" | "zone" | "bin">("warehouse");
  const [parent, setParent] = useState("");

  const effectiveFacility =
    facility.length > 0 ? facility : facilityItems.length === 1 ? (facilityItems[0]?.id ?? "") : "";
  // §2.1's one legal parent kind for what is being created — narrowed on the
  // server, so a new bin can name any of the facility's zones and not merely the
  // ones the tree behind this panel happened to have loaded.
  const parentKinds = legalParentKinds(kind);

  return (
    <section className="inv-panel" aria-label={t("inventory.locations.add")}>
      <div className="inv-panel-head">
        <h2>{t("inventory.locations.add")}</h2>
        <Button className="inv-panel-close" onClick={onClose}>
          {t("inventory.common.close")}
        </Button>
      </div>
      {create.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.locations.added")}
        </p>
      ) : null}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({
            facility_id: effectiveFacility,
            code: code.trim(),
            name: name.trim(),
            kind,
            ...(parent.length > 0 ? { parent_location_id: parent } : {}),
          });
        }}
      >
        {facilityItems.length > 1 ? (
          <FormField label={t("inventory.filters.facility")}>
            <Select
              value={facility}
              onChange={(event) => {
                setFacility(event.target.value);
              }}
              required
            >
              <option value="">{t("inventory.locations.pickFacility")}</option>
              {facilityItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </FormField>
        ) : null}
        <FormField label={t("inventory.locations.kind")}>
          <Select
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as "warehouse" | "zone" | "bin");
              setParent("");
            }}
          >
            <option value="warehouse">{t("inventory.vocab.locationKind.warehouse")}</option>
            <option value="zone">{t("inventory.vocab.locationKind.zone")}</option>
            <option value="bin">{t("inventory.vocab.locationKind.bin")}</option>
          </Select>
        </FormField>
        {parentKinds !== undefined ? (
          <LocationPicker
            testId="create-location-parent"
            label={t("inventory.locations.parent")}
            isActive
            value={parent}
            onChange={setParent}
            facilityId={effectiveFacility}
            serverKinds={parentKinds}
            emptyLabel={t(
              kind === "bin"
                ? "inventory.locations.noBinParent"
                : "inventory.locations.noZoneParent",
            )}
          />
        ) : null}
        <FormField label={t("inventory.locations.code")}>
          <input
            className="ds-input"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
            }}
            placeholder="A-03"
            required
          />
        </FormField>
        <FormField label={t("inventory.locations.name")}>
          <input
            className="ds-input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
          />
        </FormField>
        {create.isError ? (
          <p className="form-error" role="alert">
            {inventoryErrorMessage(t, create.error, "inventory.locations.addFailed")}
          </p>
        ) : null}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            busy={create.isPending}
            // A non-warehouse NEEDS a parent (§2.1); the picker is not a native
            // `required` control, so the submit states the rule itself rather
            // than letting the server answer LOCATION_HIERARCHY_INVALID.
            disabled={
              effectiveFacility.length === 0 || (parentKinds !== undefined && parent.length === 0)
            }
            title={
              effectiveFacility.length === 0
                ? t("inventory.locations.pickFacility")
                : parentKinds !== undefined && parent.length === 0
                  ? t("inventory.locations.parentRequired")
                  : undefined
            }
          >
            {t("inventory.locations.addSubmit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** §2.1's legal parents of a kind, narrowed on the server one kind at a time
 * (task #253, D-330): a zone hangs under a warehouse, and a bin under a zone or
 * directly under a warehouse, which is what the server's hierarchy rule has
 * always allowed and what both pickers used to refuse. A warehouse is a root. */
function legalParentKinds(kind: string): readonly string[] | undefined {
  if (kind === "zone") return ["warehouse"];
  if (kind === "bin") return ["zone", "warehouse"];
  return undefined;
}
